import { useState, useMemo, useCallback, useEffect } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, AlertCircle, ArrowRight, CheckCircle, Banknote, CreditCard, Clock, TrendingDown } from "lucide-react";
import { useToolLifecycle } from "@/utils/trackingHooks";
import { BLENDED_IN_PERSON, BLENDED_ONLINE } from "@/utils/pricingEngine";
import LeadForm from "@/forms/engine/LeadForm";
import toolCashVsCard, { CASH_VS_CARD_INPUT_LABELS } from "@/forms/definitions/toolCashVsCard";

const CVC_ITEM_NAME = "cash_vs_card_comparator";
const CVC_ITEM_OPTS = { itemType: "comparison" };

const DEFAULT_CARD_MIX = { debit: 0.70, credit: 0.20, business: 0.10 };

function formatGBP(v) { return new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP", minimumFractionDigits: 2 }).format(v); }
function formatHours(v) { const r = Math.round(v * 10) / 10; return `${r} hr${r !== 1 ? "s" : ""}`; }

function parseCashInputs(inputs) {
  const ms = parseFloat(inputs.monthlySales) || 0;
  const atv = parseFloat(inputs.averageTransactionValue) || 0;
  const cpct = Math.max(0, Math.min(100, parseFloat(inputs.cashPercentage) || 0));
  const cashSales = ms * (cpct / 100);
  const cardSales = ms - cashSales;
  const cardTransactions = cardSales / atv;
  return { ms, atv, cashSales, cardSales, cardTransactions };
}

function calcCashHandlingCost(inputs, cashSales) {
  const wbh = ((parseFloat(inputs.bankTripsPerWeek) || 0) * (parseFloat(inputs.minutesPerBankTrip) || 0)) / 60;
  const wch = ((parseFloat(inputs.cashCountingMinutesPerDay) || 0) * (parseFloat(inputs.daysOpenPerWeek) || 0)) / 60;
  const totalHrs = (wbh + wch) * 4.33;
  const labourCost = totalHrs * (parseFloat(inputs.hourlyWage) || 0);
  const depositCost = cashSales * ((parseFloat(inputs.cashDepositFeePercent) || 0) / 100);
  const lossCost = cashSales * ((parseFloat(inputs.cashLossPercent) || 0) / 100);
  return { labourCost, depositCost, lossCost, totalHrs, totalCashCost: labourCost + depositCost + lossCost };
}

function calcCardProcessingCost(inputs, cardSales, cardTransactions) {
  const mix = inputs.cardMixMode === "advanced"
    ? { debit: (parseFloat(inputs.debitPct) || 0) / 100, credit: (parseFloat(inputs.creditPct) || 0) / 100, business: (parseFloat(inputs.businessPct) || 0) / 100 }
    : DEFAULT_CARD_MIX;
  const rates = inputs.pricingMode === "online" ? BLENDED_ONLINE : BLENDED_IN_PERSON;
  const authCost = cardTransactions * rates.authFee;
  const gwCost = inputs.pricingMode === "online" ? cardTransactions * rates.gatewayFeePerTransaction : 0;
  const totalCardCost = cardSales * mix.debit * rates.debit + cardSales * mix.credit * rates.credit + cardSales * mix.business * rates.business + authCost + gwCost;
  return totalCardCost;
}

function categoriseDiff(diff) {
  if (diff > 50) return { category: "cards_advantage", headline: "Card payments may be costing less than handling cash", description: "Based on the figures entered, the hidden cost of handling cash appears higher than the estimated cost of taking cards." };
  if (diff < -50) return { category: "cash_advantage", headline: "Cash handling appears cheaper on these assumptions", description: "Based on the figures entered, cash currently appears less expensive than the card pricing used in this comparison. A tailored review may still identify different pricing or operational factors." };
  return { category: "broadly_similar", headline: "Cash and card costs look broadly similar", description: "Based on the figures entered, the monthly cost difference appears relatively small. A more tailored review may still highlight a better option." };
}

const CASH_VS_CARD_CATEGORY_LABEL = {
  cards_advantage: "Cards advantage",
  cash_advantage: "Cash advantage",
  broadly_similar: "Broadly similar",
};

const PRICING_MODE_LABEL = {
  in_person: "In person",
  online: "Online",
};

// Example figures the comparator opens with (results show straight away).
const DEFAULT_INPUTS = {
  monthlySales: "20000", averageTransactionValue: "20", cashPercentage: "50",
  bankTripsPerWeek: "2", minutesPerBankTrip: "30", cashCountingMinutesPerDay: "15",
  daysOpenPerWeek: "6", hourlyWage: "12", cashDepositFeePercent: "0.5", cashLossPercent: "0.5",
  cardMixMode: "default", debitPct: "", creditPct: "", businessPct: "", pricingMode: "in_person",
};

const INPUT_LABELS = {
  ...CASH_VS_CARD_INPUT_LABELS,
  cashPercentage: "Cash share of sales",
  bankTripsPerWeek: "Bank trips per week",
  minutesPerBankTrip: "Minutes per bank trip",
  cashCountingMinutesPerDay: "Cash counting minutes per day",
  daysOpenPerWeek: "Days open per week",
  hourlyWage: "Staff hourly wage",
  cashDepositFeePercent: "Deposit fee %",
  cashLossPercent: "Shrinkage %",
  pricingMode: "Card pricing",
};

const numberOrUndefined = (value) => {
  if (value === "" || value === null || value === undefined) return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
};
const round2 = (n) => (Number.isFinite(n) ? Math.round(n * 100) / 100 : undefined);
const compact = (obj) =>
  Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined && v !== null && v !== ""));

function cardMixText(inputs) {
  if (inputs.cardMixMode !== "advanced") {
    const pct = (v) => Math.round(v * 100);
    return `Standard (${pct(DEFAULT_CARD_MIX.debit)}% debit / ${pct(DEFAULT_CARD_MIX.credit)}% credit / ${pct(DEFAULT_CARD_MIX.business)}% business)`;
  }
  const v = (x) => numberOrUndefined(x) || 0;
  return `Custom (${v(inputs.debitPct)}% debit / ${v(inputs.creditPct)}% credit / ${v(inputs.businessPct)}% business)`;
}

/** Comparator inputs + result as readable `context` for the Lead (see toolCashVsCard.jsx). */
function buildLeadContext(inputs, result) {
  const leftAtDefault = Object.keys(INPUT_LABELS)
    .filter((key) => inputs[key] === DEFAULT_INPUTS[key])
    .map((key) => INPUT_LABELS[key]);
  return compact({
    tool: "Cash vs Card Comparator",
    category: CASH_VS_CARD_CATEGORY_LABEL[result.category] || result.category,
    cash_handling_cost_gbp_per_month: round2(result.totalCashCost),
    card_processing_cost_gbp_per_month: round2(result.totalCardCost),
    // Positive: cards cost less than handling cash; negative: cash is cheaper.
    cash_minus_card_gbp_per_month: round2(result.diff),
    cash_minus_card_gbp_per_year: round2(result.annualDiff),
    cash_labour_cost_gbp_per_month: round2(result.labourCost),
    cash_deposit_fees_gbp_per_month: round2(result.depositCost),
    cash_losses_gbp_per_month: round2(result.lossCost),
    cash_handling_hours_per_month: Math.round(result.totalHrs * 10) / 10,
    card_transactions_per_month: Math.round(result.cardTransactions),
    monthly_sales_gbp: numberOrUndefined(inputs.monthlySales),
    average_transaction_value_gbp: numberOrUndefined(inputs.averageTransactionValue),
    cash_share_pct: numberOrUndefined(inputs.cashPercentage),
    bank_trips_per_week: numberOrUndefined(inputs.bankTripsPerWeek),
    minutes_per_bank_trip: numberOrUndefined(inputs.minutesPerBankTrip),
    cash_counting_minutes_per_day: numberOrUndefined(inputs.cashCountingMinutesPerDay),
    days_open_per_week: numberOrUndefined(inputs.daysOpenPerWeek),
    staff_hourly_wage_gbp: numberOrUndefined(inputs.hourlyWage),
    cash_deposit_fee_pct: numberOrUndefined(inputs.cashDepositFeePercent),
    cash_loss_pct: numberOrUndefined(inputs.cashLossPercent),
    card_pricing: PRICING_MODE_LABEL[inputs.pricingMode] || inputs.pricingMode,
    card_mix: cardMixText(inputs),
    left_at_default: leftAtDefault.length ? leftAtDefault : undefined,
  });
}

function calculateCashVsCard(inputs) {
  const { ms, atv, cashSales, cardSales, cardTransactions } = parseCashInputs(inputs);
  if (ms <= 0 || atv <= 0) return null;

  const cash = calcCashHandlingCost(inputs, cashSales);
  const totalCardCost = calcCardProcessingCost(inputs, cardSales, cardTransactions);
  const diff = cash.totalCashCost - totalCardCost;

  return {
    ...cash, totalCardCost, cardTransactions, diff,
    annualDiff: diff * 12, ...categoriseDiff(diff),
  };
}

function CurrencyInput({ label, name, value, onChange, placeholder, testId, helper }) {
  return (
    <div>
      <label className="block text-sm font-semibold text-brand-black mb-1">{label}</label>
      {helper && <p className="text-xs text-brand-grey mb-1">{helper}</p>}
      <div className="relative"><span className="absolute left-4 top-1/2 -translate-y-1/2 text-brand-grey text-sm">£</span><input type="number" name={name} value={value} onChange={onChange} placeholder={placeholder} step="any" min="0" className="w-full pl-8 pr-4 py-3 rounded-xl border border-brand-light-grey bg-white text-brand-black text-sm focus:outline-none focus:ring-2 focus:ring-brand-aqua/30 focus:border-brand-aqua transition-all" data-testid={testId} /></div>
    </div>
  );
}

function NumInput({ label, name, value, onChange, placeholder, testId, suffix, step = "1" }) {
  return (
    <div>
      <label className="block text-sm font-semibold text-brand-black mb-1.5">{label}</label>
      <div className="relative"><input type="number" name={name} value={value} onChange={onChange} placeholder={placeholder} step={step} min="0" className={`w-full pl-4 ${suffix ? "pr-12" : "pr-4"} py-3 rounded-xl border border-brand-light-grey bg-white text-brand-black text-sm focus:outline-none focus:ring-2 focus:ring-brand-aqua/30 focus:border-brand-aqua transition-all`} data-testid={testId} />{suffix && <span className="absolute right-4 top-1/2 -translate-y-1/2 text-brand-grey text-xs">{suffix}</span>}</div>
    </div>
  );
}

function PctInput({ label, name, value, onChange, placeholder, testId }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-brand-black mb-1">{label}</label>
      <div className="relative"><input type="number" name={name} value={value} onChange={onChange} placeholder={placeholder} step="any" min="0" max="100" className="w-full pl-4 pr-8 py-2.5 rounded-xl border border-brand-light-grey bg-white text-brand-black text-sm focus:outline-none focus:ring-2 focus:ring-brand-aqua/30 focus:border-brand-aqua transition-all" data-testid={testId} /><span className="absolute right-3 top-1/2 -translate-y-1/2 text-brand-grey text-sm">%</span></div>
    </div>
  );
}

function ResultCard({ icon: Icon, label, value, variant = "default", testId }) {
  const bg = { default: "bg-white border border-brand-light-grey", cash: "bg-brand-pink-light border border-brand-pink/20", card: "bg-brand-aqua-light border border-brand-aqua/20", highlight: "bg-gradient-to-br from-brand-aqua-light to-white border border-brand-aqua/30", negative: "bg-brand-pink-light border border-brand-pink/20" };
  const text = { default: "text-brand-black", cash: "text-brand-pink", card: "text-brand-aqua-dark", highlight: "text-brand-aqua-dark", negative: "text-brand-pink" };
  return (
    <div className={`${bg[variant]} rounded-xl p-4`} data-testid={testId}>
      <div className="flex items-center gap-2 mb-1">{Icon && <Icon size={14} className="text-brand-grey" />}<p className="text-xs font-bold uppercase tracking-wider text-brand-grey">{label}</p></div>
      <p className={`text-xl sm:text-2xl font-heading font-bold ${text[variant]}`}>{value}</p>
    </div>
  );
}

function ResultsPanel({ result }) {
  const diffPositive = result.diff > 0;
  const catConfig = { cards_advantage: { icon: CreditCard, color: "text-brand-aqua-dark", bg: "bg-brand-aqua-light border-brand-aqua/20" }, cash_advantage: { icon: Banknote, color: "text-brand-pink", bg: "bg-brand-pink-light border-brand-pink/20" }, broadly_similar: { icon: CheckCircle, color: "text-amber-500", bg: "bg-amber-50 border-amber-200" } };
  const cfg = catConfig[result.category];
  const Icon = cfg.icon;

  return (
    <div className="space-y-4 pt-2" data-testid="cvc-result">
      <div className="grid grid-cols-2 gap-3">
        <div className="result-card-animate"><ResultCard icon={Banknote} label="Monthly cash handling cost" value={formatGBP(result.totalCashCost)} variant="cash" testId="cvc-cash-cost" /></div>
        <div className="result-card-animate"><ResultCard icon={CreditCard} label="Monthly card processing cost" value={formatGBP(result.totalCardCost)} variant="card" testId="cvc-card-cost" /></div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="result-card-animate"><ResultCard label="Monthly difference" value={formatGBP(Math.abs(result.diff))} variant={diffPositive ? "highlight" : "negative"} testId="cvc-monthly-diff" /></div>
        <div className="result-card-animate"><ResultCard label="Annual difference" value={formatGBP(Math.abs(result.annualDiff))} variant={diffPositive ? "highlight" : "negative"} testId="cvc-annual-diff" /></div>
      </div>

      <div className={`rounded-xl border p-5 ${cfg.bg}`}>
        <div className="flex items-start gap-3">
          <Icon size={22} className={`${cfg.color} flex-shrink-0 mt-0.5`} />
          <div>
            <h4 className="font-heading font-bold text-brand-black mb-1" data-testid="cvc-headline">{result.headline}</h4>
            <p className="text-sm text-brand-grey leading-relaxed">{result.description}</p>
            {diffPositive && <p className="text-sm font-semibold text-brand-aqua-dark mt-2">{`Cards may save you approximately ${formatGBP(result.diff)} per month`}</p>}
            {result.diff < -50 && <p className="text-sm font-semibold text-brand-pink mt-2">{`Cash appears cheaper by approximately ${formatGBP(Math.abs(result.diff))} per month`}</p>}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <ResultCard icon={Clock} label="Cash handling hours" value={formatHours(result.totalHrs)} testId="cvc-hours" />
        <ResultCard icon={TrendingDown} label="Deposit fees" value={formatGBP(result.depositCost)} testId="cvc-deposit" />
        <ResultCard icon={TrendingDown} label="Cash losses" value={formatGBP(result.lossCost)} testId="cvc-loss" />
        <ResultCard icon={CreditCard} label="Card transactions" value={Math.round(result.cardTransactions).toLocaleString("en-GB")} testId="cvc-card-txns" />
      </div>

      <div className="flex flex-col sm:flex-row gap-3 pt-1">
        <Link to="/contact" className="flex-1 bg-brand-aqua hover:bg-brand-aqua-dark text-white font-semibold rounded-full px-6 py-3.5 text-sm flex items-center justify-center gap-2 transition-colors btn-shine" data-testid="cvc-cta-review">Get my tailored comparison <ArrowRight size={16} /></Link>
        <a href="/book" className="flex-1 bg-white border border-brand-aqua text-brand-aqua-dark font-semibold rounded-full px-6 py-3.5 text-sm flex items-center justify-center gap-2 hover:bg-brand-aqua-light transition-colors" data-testid="cvc-cta-speak">Speak to the team</a>
      </div>
    </div>
  );
}

function useCashVsCard() {
  const [inputs, setInputs] = useState(DEFAULT_INPUTS);
  const { markStarted, markCompleted } = useToolLifecycle(CVC_ITEM_NAME, CVC_ITEM_OPTS);

  const handleChange = useCallback((e) => {
    markStarted();
    const { name, value } = e.target;
    setInputs((p) => ({ ...p, [name]: value }));
  }, [setInputs]); // eslint-disable-line react-hooks/exhaustive-deps

  const mixTotal = useMemo(() => {
    if (inputs.cardMixMode !== "advanced") return 100;
    return (parseFloat(inputs.debitPct) || 0) + (parseFloat(inputs.creditPct) || 0) + (parseFloat(inputs.businessPct) || 0);
  }, [inputs.cardMixMode, inputs.debitPct, inputs.creditPct, inputs.businessPct]);
  const mixError = inputs.cardMixMode === "advanced" && mixTotal !== 100;

  const result = useMemo(() => {
    if (mixError) return null;
    return calculateCashVsCard(inputs);
  }, [inputs, mixError]);

  useEffect(() => { if (result) markCompleted(); }, [result]); // eslint-disable-line react-hooks/exhaustive-deps

  return { inputs, handleChange, setInputs, mixTotal, mixError, result };
}

function CashInputs({ inputs, handleChange }) {
  return (
    <>
      <p className="text-xs font-bold uppercase tracking-wider text-brand-aqua">Sales overview</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <CurrencyInput label="Monthly sales to compare" name="monthlySales" value={inputs.monthlySales} onChange={handleChange} placeholder="20000" testId="cvc-sales" />
        <CurrencyInput label="Average transaction value" name="averageTransactionValue" value={inputs.averageTransactionValue} onChange={handleChange} placeholder="20" testId="cvc-atv" />
      </div>
      <div>
        <label className="block text-sm font-semibold text-brand-black mb-1.5">{`Percentage of sales currently taken in cash: ${inputs.cashPercentage}%`}</label>
        <input type="range" name="cashPercentage" value={inputs.cashPercentage} onChange={handleChange} min="0" max="100" step="1" className="w-full h-2 bg-brand-light-grey rounded-full appearance-none cursor-pointer accent-brand-aqua" data-testid="cvc-cash-pct" />
        <div className="flex justify-between text-xs text-brand-grey mt-1"><span>0% (all cards)</span><span>100% (all cash)</span></div>
      </div>
      <div className="border-t border-brand-light-grey/50 pt-4" />
      <p className="text-xs font-bold uppercase tracking-wider text-brand-aqua">Cash handling costs</p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <NumInput label="Bank trips / week" name="bankTripsPerWeek" value={inputs.bankTripsPerWeek} onChange={handleChange} placeholder="2" testId="cvc-bank-trips" />
        <NumInput label="Mins per trip" name="minutesPerBankTrip" value={inputs.minutesPerBankTrip} onChange={handleChange} placeholder="30" suffix="mins" testId="cvc-trip-mins" />
        <NumInput label="Counting mins / day" name="cashCountingMinutesPerDay" value={inputs.cashCountingMinutesPerDay} onChange={handleChange} placeholder="15" suffix="mins" testId="cvc-count-mins" />
        <NumInput label="Days open / week" name="daysOpenPerWeek" value={inputs.daysOpenPerWeek} onChange={handleChange} placeholder="6" testId="cvc-days" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <CurrencyInput label="Staff hourly wage" name="hourlyWage" value={inputs.hourlyWage} onChange={handleChange} placeholder="12" testId="cvc-wage" />
        <NumInput label="Deposit fee %" name="cashDepositFeePercent" value={inputs.cashDepositFeePercent} onChange={handleChange} placeholder="0.5" suffix="%" step="0.01" testId="cvc-deposit-pct" />
        <NumInput label="Shrinkage %" name="cashLossPercent" value={inputs.cashLossPercent} onChange={handleChange} placeholder="0.5" suffix="%" step="0.01" testId="cvc-loss-pct" />
      </div>
    </>
  );
}

function CardOptions({ inputs, handleChange, setInputs, showAdvanced, setShowAdvanced, mixTotal, mixError }) {
  return (
    <>
      <div>
        <label className="block text-sm font-semibold text-brand-black mb-1.5">Which card pricing for comparison?</label>
        <div className="flex gap-2">
          <button onClick={() => setInputs((p) => ({ ...p, pricingMode: "in_person" }))} className={`flex-1 py-2.5 rounded-xl text-sm font-bold transition-all ${inputs.pricingMode === "in_person" ? "bg-brand-aqua text-white" : "bg-white border border-brand-light-grey text-brand-grey"}`} data-testid="cvc-mode-inperson">In-person</button>
          <button onClick={() => setInputs((p) => ({ ...p, pricingMode: "online" }))} className={`flex-1 py-2.5 rounded-xl text-sm font-bold transition-all ${inputs.pricingMode === "online" ? "bg-brand-aqua text-white" : "bg-white border border-brand-light-grey text-brand-grey"}`} data-testid="cvc-mode-online">Online</button>
        </div>
      </div>
      <button onClick={() => setShowAdvanced(!showAdvanced)} className="flex items-center gap-2 text-sm font-semibold text-brand-aqua hover:text-brand-aqua-dark transition-colors" data-testid="cvc-mix-toggle">
        {inputs.cardMixMode === "advanced" ? "Use standard card mix" : "Enter my own card mix"}
        <ChevronDown size={16} className={`transition-transform ${showAdvanced ? "rotate-180" : ""}`} />
      </button>
      {showAdvanced && (
        <div className="bg-gray-50 rounded-xl p-4 border border-brand-light-grey/50 space-y-3">
          <div className="flex gap-2">
            <button onClick={() => setInputs((p) => ({ ...p, cardMixMode: "default" }))} className={`flex-1 py-2 rounded-lg text-xs font-bold transition-all ${inputs.cardMixMode === "default" ? "bg-brand-aqua text-white" : "bg-white border border-brand-light-grey text-brand-grey"}`} data-testid="cvc-mix-default">Standard</button>
            <button onClick={() => setInputs((p) => ({ ...p, cardMixMode: "advanced" }))} className={`flex-1 py-2 rounded-lg text-xs font-bold transition-all ${inputs.cardMixMode === "advanced" ? "bg-brand-aqua text-white" : "bg-white border border-brand-light-grey text-brand-grey"}`} data-testid="cvc-mix-advanced">Custom</button>
          </div>
          {inputs.cardMixMode === "advanced" && (
            <>
              <div className="grid grid-cols-3 gap-2">
                <PctInput label="Debit" name="debitPct" value={inputs.debitPct} onChange={handleChange} placeholder="70" testId="cvc-debit-pct" />
                <PctInput label="Credit" name="creditPct" value={inputs.creditPct} onChange={handleChange} placeholder="20" testId="cvc-credit-pct" />
                <PctInput label="Business" name="businessPct" value={inputs.businessPct} onChange={handleChange} placeholder="10" testId="cvc-biz-pct" />
              </div>
              <div className={`text-xs font-semibold text-center ${mixError ? "text-red-500" : "text-brand-aqua"}`} data-testid="cvc-mix-total">{`Total: ${mixTotal}% ${mixError ? "— must equal 100%" : ""}`}</div>
            </>
          )}
        </div>
      )}
    </>
  );
}

export default function CashVsCardComparator({ className = "" }) {
  const { inputs, handleChange, setInputs, mixTotal, mixError, result } = useCashVsCard();
  const leadContext = useMemo(() => (result ? buildLeadContext(inputs, result) : null), [inputs, result]);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const hasMinInput = (parseFloat(inputs.monthlySales) || 0) > 0 && (parseFloat(inputs.averageTransactionValue) || 0) > 0;

  return (
    <div className={`bg-white border border-brand-light-grey rounded-2xl shadow-sm overflow-hidden ${className}`} data-testid="cash-vs-card-comparator">
      <div className="bg-brand-aqua-light px-6 sm:px-8 py-5 flex items-center gap-3 border-b border-brand-aqua/10">
        <div className="w-10 h-10 rounded-xl bg-brand-aqua flex items-center justify-center"><Banknote size={20} className="text-white" /></div>
        <div>
          <h3 className="font-heading text-lg font-bold text-brand-black">Cash vs Card Comparator</h3>
          <p className="text-xs text-brand-grey">Compare the cost of handling cash versus taking card payments</p>
        </div>
      </div>

      <div className="p-6 sm:p-8 space-y-5">
        <CashInputs inputs={inputs} handleChange={handleChange} />
        <div className="border-t border-brand-light-grey/50 pt-4" />
        <p className="text-xs font-bold uppercase tracking-wider text-brand-aqua">Card pricing</p>
        <CardOptions inputs={inputs} handleChange={handleChange} setInputs={setInputs} showAdvanced={showAdvanced} setShowAdvanced={setShowAdvanced} mixTotal={mixTotal} mixError={mixError} />
        {!hasMinInput && (<div className="flex items-start gap-2 text-xs text-brand-grey bg-gray-50 rounded-xl p-3"><AlertCircle size={14} className="flex-shrink-0 mt-0.5" />Enter monthly sales and average transaction value to see the comparison.</div>)}
        {result && <ResultsPanel result={result} />}
        {result && (
          <div className="border-t border-brand-light-grey/60 pt-6">
            <h4 className="font-heading text-lg font-bold text-brand-black text-center mb-1">Get your personalised review</h4>
            <p className="text-xs text-brand-grey text-center mb-4">Tell us where to send your tailored follow-up.</p>
            <LeadForm schema={toolCashVsCard} context={leadContext} variant="compact" />
          </div>
        )}
        <p className="text-[11px] text-brand-grey leading-relaxed text-center">This comparison is illustrative and based on the assumptions and values entered. It does not include every operational factor and is not a formal quote or financial recommendation. A tailored review may produce different results depending on actual pricing, card mix, banking arrangements, and business operations.</p>
      </div>
    </div>
  );
}
